import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { createSearchParams, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Modal as DocumentUpload } from '@creditsights/document-upload-web';
import { ROUTES, USER_ROUTES } from './Base.constants';
import Header from './components/Header';
import Navigation from './components/Header/Navigation';
import OnboardingAlert from './components/Header/Navigation/OnboardingAlert';
import Help from './components/Help/Help';
import Translation from './components/Translation';
import useIsAuthenticated from './hooks/auth/useIsAuthenticated';
import useFetchFirstLogin from './hooks/firstLogin/useFetchFirstLogin';
import useFetchUserPermissions from './hooks/permissions/useFetchUserPermissions';
import useFetchUserProfile from './hooks/profile/useFetchUserProfile';

export const ONBOARDING_SEARCH_PARAM = 'onboarding-completed';

const Base = (): React.ReactElement | null => {
  const [searchParams, setSearchParams] = useSearchParams();
  const isOnboardingParamExists = useMemo(() => searchParams.get(ONBOARDING_SEARCH_PARAM) === 'true', [searchParams]);
  const [showOnboardingAlert, setShowOnboardingAlert] = useState<boolean>(false);
  const queryClient = useQueryClient();
  const { isAuthenticated } = useIsAuthenticated(queryClient);
  const { data: userPermissions } = useFetchUserPermissions(isAuthenticated);

  const [isOnboardingCheckDone, setIsOnboardingCheckDone] = useState(false);
  const { pathname } = useLocation();
  const onboardingRoute = `${ROUTES.user}/${USER_ROUTES.onboarding}`;
  const isOnboardingRoute = pathname === onboardingRoute;
  const { data: profile } = useFetchUserProfile(isAuthenticated);
  const navigate = useNavigate();
  const { data: isFirstLogin } = useFetchFirstLogin(isOnboardingCheckDone && !isOnboardingRoute && isAuthenticated);
  const [documentUploadOpen, setDocumentUploadOpen] = useState(false);

  const handleOpenRequestResearch = useMemo(() => {
    if (userPermissions?.permissions?.some((item) => item.startsWith('content:covenants:'))) {
      return () => setDocumentUploadOpen(true);
    }

    return undefined;
  }, [userPermissions]);

  const getUpdatedSearchParams = (searchParams: URLSearchParams) => {
    const newSearchParams = createSearchParams(searchParams);
    newSearchParams.delete(ONBOARDING_SEARCH_PARAM);
    return newSearchParams;
  };

  useLayoutEffect(() => {
    const hasPermission = userPermissions?.hasAccessToOnboarding();
    if (profile && !profile.onboardingCompleted && hasPermission) {
      navigate(`${ROUTES.user}/${USER_ROUTES.onboarding}`, { replace: true });
    }
    // Setting Timeout because we need to make sure that "isOnboardingCheckDone" is always set after forced navigation is accomplished
    // Forced navigation happens when user hasn't completed Onboarding flow
    setTimeout(() => {
      setIsOnboardingCheckDone(true);
    }, 50);
  }, [profile]);

  useEffect(() => {
    if (!isOnboardingParamExists || !profile?.onboardingCompleted) {
      return;
    }
    setSearchParams(getUpdatedSearchParams(searchParams));
    setShowOnboardingAlert(true);
    setTimeout(() => setShowOnboardingAlert(false), 5000);
  }, [isOnboardingParamExists, profile]);

  if (!isAuthenticated) {
    return null;
  }
  return (
    <>
      <Header isFirstLogin={isFirstLogin} onOpenRequestResearch={handleOpenRequestResearch} />
      <Navigation />
      {showOnboardingAlert && <OnboardingAlert onClose={() => setShowOnboardingAlert(false)} />}
      <Help />
      <Translation />
      {documentUploadOpen ? <DocumentUpload onClose={() => setDocumentUploadOpen(false)} /> : null}
    </>
  );
};

export default Base;
